import { FC, useMemo } from "react";
import { getClients } from "@payping/redux/actions";
import { ensureString } from "@payping/utilities-shared/string";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import {
  Button,
  Col,
  Collapse,
  Descriptions,
  Drawer,
  Flex,
  Row,
  Skeleton,
  Space,
  Tag,
  Timeline,
  Typography,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import { ClockCircleOutlined, CuToman } from "@payping/icons";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { ServiceType, invoiceV2Apis, paymentV3Apis, permalinkApis, timelineApis, uploadV2Apis } from "@payping/api";
import { useAsync, useAsyncFn, useAsyncRetry } from "react-use";
import { ValidEmail, ValidMobileNumber } from "@payping/utils/validation";
import { useAppDispatch } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useEnumTranslation } from "@/utils/useEnumTranslation";
import { getCorrectDate } from "@/utils/getCorrectDate";
import { useLastUrlWasPage } from "@/utils";
import { CreateRefundAction, RetryRefundAction } from "@/refund/components";
import { REFUND_STATE_COLOR_DICTIONARY } from "@/refund/constants/dictionary.constant";
import { useGetLatestRefundInfoByPaymentCode } from "@/refund/use-cases";
import { MIN_AMOUNT } from "@/refund/constants/general.constant";

interface Props {
  code: string;
  open?: boolean;
  onClose: () => void;
}

export const TransactionDetails: FC<Props> = ({ code, onClose, open }) => {
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation();
  const { getPaymentStatus } = useEnumTranslation();

  const { breakpoints } = usePaypingContext();
  const { md } = breakpoints;
  const { navigateToLast } = useLastUrlWasPage();

  const { value: details, loading } = useAsyncRetry(async () => {
    try {
      const response = await paymentV3Apis.reportGetPaymentReportDetailWithPaymentCodeGet(code);
      return response;
    } catch {
      navigateToLast();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);
  const paymentCode = details?.code ?? "";
  const payload = details?.amount && details?.amount >= MIN_AMOUNT && details.code ? paymentCode : "";
  const refundInfo = useGetLatestRefundInfoByPaymentCode(payload);

  const { value: clients } = useAsync(() => dispatch(getClients()), [dispatch]);

  const { value: itemBuyer } = useAsync(async () => {
    if (details?.clientId !== "PaypingProduct") {
      return Promise.reject();
    }
    return permalinkApis.permaLinkGetBuyerDetailByPaymentCode(paymentCode, { isSilent: true });
  }, [paymentCode, details]);

  const { value: invoiceBuyer } = useAsync(async () => {
    const invoiceCode = details?.clientRefId;
    if (details?.clientId !== "PaypingInvoice" || !invoiceCode) {
      return Promise.reject();
    }
    return invoiceV2Apis.invoiceBuyer(invoiceCode, { isSilent: true });
  }, [details]);

  const { value: timeLine } = useAsync(
    () => timelineApis.v1TimeLineListGet(ServiceType.NUMBER_1, paymentCode),
    [paymentCode],
  );

  // TODO: this is a temporary solution for users who have wage on payer side (they can't see payer details inside transaction details page), this should be fixed in new payment
  const [, handlePdf] = useMessagedAsyncFn(
    async (newWindow: Window | null) => {
      if (details?.clientRefId) {
        const path = await uploadV2Apis.productBuyersWithProductCodeGet(details?.clientRefId);
        newWindow?.location?.assign?.(path);
      }
    },
    [details?.clientRefId],
    undefined,
  );

  const [{ loading: getExportLoading }, getExport] = useAsyncFn(async () => {
    const url = await uploadV2Apis.paymentReportPdfWithPayCodeGet(paymentCode);
    return url;
  }, [paymentCode]);

  const { name: statusText, color: statusColor } = getPaymentStatus(details?.paymentStatus);

  const { string: payDateFa } = toJalali({
    date: getCorrectDate(details?.payDate),
    toFormat: dateFormats.ui.jalaliDateTime,
  });

  const wage = useMemo(() => {
    let text = "";
    if (!details?.bWage && !details?.pWage)
      return ensureString({ text: t("report.sidebar.zeroWage"), fallbackValue: "-" });
    if (details?.bWage)
      text += `${formatLocalizedNumber({ value: details?.bWage, options: { maximumFractionDigits: 2 } })} ${t("toman")} ${t("report.sidebar.bWage")}`;
    if (details?.bWage && details?.pWage) text += " / ";
    if (details?.pWage)
      text += `${formatLocalizedNumber({ value: details?.pWage, options: { maximumFractionDigits: 2 } })} ${t("toman")} ${t("report.sidebar.pWage")}`;
    return text;
  }, [details, t]);

  const clientIdName = clients?.find(({ clientId }) => clientId === details?.clientId)?.displayName;

  const timeLineContent = (
    <Timeline
      items={timeLine?.map((item) => ({
        children: (
          <div className="tw:pr-3!" key={item.code ?? ""}>
            <p className={"color--gray500"}>
              {toJalali({ date: item.eventDate, toFormat: "HH:mm jYYYY/jMM/jDD" }).string}
              <ClockCircleOutlined width={12} className={"color--gray500 tw:mr-1.25! tw:mt-1!"} />
            </p>
            <p className="tw:-mt-3!" style={{ color: "#202124" }}>
              {item.title}
            </p>
            <div
              className="tw:mb-3.75! tw:w-full tw:rounded-lg tw:p-3.75!"
              style={{
                backgroundColor: "#fff",
              }}
            >
              <span style={{ color: "#666666" }}>{item.description}</span>
            </div>
          </div>
        ),
      }))}
    ></Timeline>
  );

  const onPrintTransactionClick = async (newWindow: Window | null): Promise<void> => {
    const url = await getExport();
    newWindow?.location?.assign?.(url);
  };

  const printButton = (
    <Button
      onClick={() => {
        const newWindow = window.open(undefined, "_blank");
        onPrintTransactionClick(newWindow);
      }}
      loading={getExportLoading}
    >
      {t("report.print")}
    </Button>
  );

  return (
    <Drawer
      open={open}
      extra={printButton}
      title={t("report.transactionDetails")}
      placement={md ? "left" : "bottom"}
      onClose={onClose}
      size={md ? "33rem" : "100%"}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {loading ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions
              className="tw:w-full"
              column={1}
              bordered
              title={
                <Flex vertical>
                  <Space className={"tw:flex tw:justify-between"}>
                    <Space orientation="vertical">
                      {t("report.sidebar.paymentsDetails")}
                      <Flex align="center">
                        <Typography.Text>{`${formatLocalizedNumber({ value: details?.amount ?? 0 })}`}</Typography.Text>
                        <CuToman width={24} height={24} />
                      </Flex>
                    </Space>
                    <div className={"ltr"}>
                      <Typography.Text type={"secondary"}>
                        {ensureString({ text: payDateFa ?? "", fallbackValue: "-" })}
                      </Typography.Text>
                    </div>
                  </Space>
                  <Flex className="tw:mt-4!" gap="small">
                    <Button type="primary" disabled>
                      {t("report.sidebar.inquiryPayer")}
                    </Button>
                    <Button disabled>{t("report.sidebar.inquiryTransaction")}</Button>
                    <CreateRefundAction details={details} refundInfo={refundInfo.data} loading={refundInfo.isLoading} />
                    {/* <CancelRefundAction details={data} refundInfo={refundInfo.data} /> */}
                    <RetryRefundAction details={details} refundInfo={refundInfo.data} />
                  </Flex>
                </Flex>
              }
            >
              <Descriptions.Item label={t("report.sidebar.amount")}>
                <Flex align="center">
                  <Typography.Text type="secondary">{`${formatLocalizedNumber({ value: details?.amount ?? 0 })}`}</Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.status")}>
                <Tag color={statusColor}>{ensureString({ text: statusText ?? "", fallbackValue: "-" })}</Tag>
              </Descriptions.Item>
              {!refundInfo.error && refundInfo.data && (
                <Descriptions.Item label={t("report.sidebar.refundStaus")}>
                  <Tag color={REFUND_STATE_COLOR_DICTIONARY[refundInfo.data?.state ?? "0"]}>
                    {refundInfo.data.localizedState}
                  </Tag>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t("report.sidebar.invoiceNo")}>
                {ensureString({ text: details?.refId ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.code")}>
                {ensureString({ text: details?.code ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.wage")}>{wage}</Descriptions.Item>
              {/* <Descriptions.Item label={t("report.sidebar.code")}>{ensureString({ text: code ?? "", fallbackValue: "-" })}</Descriptions.Item> */}
              <Descriptions.Item label={t("report.sidebar.name")}>
                {ensureString({ text: details?.payerName ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.rrn")}>
                <div className={"ltr"}>{ensureString({ text: details?.cardNo ?? "", fallbackValue: "-" })}</div>
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.clientId")}>
                {ensureString({ text: clientIdName ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.description")}>
                {ensureString({ text: details?.description ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Col>
        <Col span={24}>
          {loading ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions className="tw:w-full" column={1} bordered title={t("report.sidebar.paymentsDetails")}>
              <Descriptions.Item label={t("report.sidebar.amount")}>
                {formatLocalizedNumber({ value: details?.amount ?? 0 })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.payDate")}>
                <div className={"ltr"}>{ensureString({ text: payDateFa ?? "", fallbackValue: "-" })}</div>
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.invoiceNo")}>
                {ensureString({ text: details?.refId ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.wage")}>{wage}</Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.shaparakWage")}>
                {details?.shaparakWage
                  ? formatLocalizedNumber({ value: details.shaparakWage, options: { maximumFractionDigits: 2 } })
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.vat")}>
                {details?.paypingVat
                  ? formatLocalizedNumber({ value: details.paypingVat, options: { maximumFractionDigits: 2 } })
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.couponName")}>
                {ensureString({ text: itemBuyer?.couponName ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.ipgName")}>
                {ensureString({ text: details?.ipgName ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Col>
        <Col span={24}>
          {loading ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions className="tw:w-full" column={1} bordered title={t("report.sidebar.customer.detail")}>
              <Descriptions.Item label={t("report.sidebar.name")}>
                {ensureString({ text: details?.payerName ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.customer.phone")}>
                {ensureString({
                  text:
                    (itemBuyer || invoiceBuyer)?.phone ||
                    (ValidMobileNumber(details?.payerIdentity) ? details?.payerIdentity : "") ||
                    "",
                  fallbackValue: "-",
                })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.customer.email")}>
                {ensureString({
                  text:
                    (itemBuyer || invoiceBuyer)?.email ||
                    (ValidEmail(details?.payerIdentity) ? details?.payerIdentity : "") ||
                    "",
                  fallbackValue: "-",
                })}
              </Descriptions.Item>
              {invoiceBuyer ? <></> : null}
              {itemBuyer || invoiceBuyer ? (
                <>
                  <Descriptions.Item label={t("report.sidebar.invoiceBuyer.nationalId")}>
                    {ensureString({ text: invoiceBuyer?.nationalId ?? "", fallbackValue: "-" })}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("report.sidebar.customer.city")}>
                    {ensureString({ text: (itemBuyer || invoiceBuyer)?.city ?? "", fallbackValue: "-" })}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("report.sidebar.invoiceBuyer.customerCode")}>
                    {ensureString({ text: invoiceBuyer?.code ?? "", fallbackValue: "-" })}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("report.sidebar.customer.customDescription")}>
                    {ensureString({ text: itemBuyer?.customDescription ?? "", fallbackValue: "-" })}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("report.sidebar.customer.customerAddressLine")}>
                    {ensureString({ text: itemBuyer?.addressLine ?? "", fallbackValue: "-" })}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("report.sidebar.customer.postalCode")}>
                    {ensureString({ text: itemBuyer?.postalCode ?? "", fallbackValue: "-" })}
                  </Descriptions.Item>
                </>
              ) : null}
            </Descriptions>
          )}
        </Col>
        <Col span={24}>
          {loading && details?.clientId === "PaypingInvoice" ? (
            <Skeleton active paragraph />
          ) : (
            <Descriptions className="tw:w-full" column={1} bordered title={t("report.sidebar.customer.serviceDetails")}>
              <Descriptions.Item label={t("report.sidebar.clientId")}>
                {ensureString({ text: clientIdName ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.invoiceBuyer.code")}>
                {details?.clientRefId}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.description")}>
                {ensureString({ text: details?.description ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Col>
        {itemBuyer?.productInfo ? (
          <Col span={24}>
            <Descriptions className="tw:w-full" column={1} bordered title={t("report.sidebar.item.detail")}>
              <Descriptions.Item label={t("report.sidebar.item.code")}>{itemBuyer.productInfo.code}</Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.item.title")}>
                {itemBuyer.productInfo.title}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.item.quantity")}>{itemBuyer.quantity}</Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.item.itemPrice")}>
                {formatLocalizedNumber({ value: itemBuyer.productInfo.realAmount ?? 0 })}
              </Descriptions.Item>
              <Descriptions.Item label={t("report.sidebar.description")}>
                {ensureString({ text: itemBuyer.productInfo.description ?? "", fallbackValue: "-" })}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        ) : null}

        {details?.clientId === "PaypingProduct" && !itemBuyer?.productInfo ? (
          <Col span={24}>
            <Descriptions className="tw:w-full" column={1} bordered title={t("report.sidebar.item.detail")}>
              <Descriptions.Item label={t("report.title")}>
                <Typography.Link
                  onClick={() => {
                    const newWindow = window.open(undefined, "_blank");
                    handlePdf(newWindow);
                  }}
                >
                  {t("report.sidebar.downloadProductPdfReport")}
                </Typography.Link>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        ) : null}
        <Col span={24}>
          <Collapse>
            <Collapse.Panel header={t("report.transactionTimeline")} key={0}>
              {timeLineContent}
            </Collapse.Panel>
          </Collapse>
        </Col>
      </Row>
    </Drawer>
  );
};

export default TransactionDetails;
